import { S3_VIDEO_SOURCE_API_PATH } from 'constants/api';
import { S3_BUCKET_NAME } from 'constants/constant';
import { ApiService } from 'service/api/apiService';
import {
  BatchJobResponse,
  HierarchicalS3Item,
  S3BrowseResponseType,
  S3ItemType,
} from '../type/videoSourceType';

class VideoSourceService {
  constructor(private readonly apiService: ApiService<S3BrowseResponseType>) {}

  public async fetchRootDirectories(): Promise<S3ItemType[]> {
    const response: S3BrowseResponseType = await this.fetch(
      new URLSearchParams({
        bucket_name: S3_BUCKET_NAME,
        prefix: 'backedup/',
      })
    );
    const directories: S3ItemType[] = (response.items || []).map((prefix: S3ItemType) => ({
      Key: prefix.Key!,
      isDirectory: true,
    }));

    const files: S3ItemType[] = (response.items || []).map((obj: S3ItemType) => ({
      Key: obj.Key!,
      isDirectory: false,
      Size: obj.Size,
      LastModified: obj.LastModified,
      ETag: obj.ETag,
      StorageClass: obj.StorageClass,
    }));

    return [...directories, ...files];
  }

  public async fetchSubItems(prefix?: string, bucketName?: string): Promise<S3ItemType[]> {
    try {
      const response: S3BrowseResponseType = await this.fetch(
        new URLSearchParams({
          bucket_name: bucketName || S3_BUCKET_NAME,
          prefix: prefix || '',
        })
      );

      const directories: S3ItemType[] = (response.items || [])
        .filter((item: S3ItemType) => item.isDirectory)
        .map((item: S3ItemType) => ({
          Key: item.Key!,
          isDirectory: true,
        }));

      const files: S3ItemType[] = (response.items || [])
        .filter((obj: S3ItemType) => !obj.isDirectory)
        .filter((obj: S3ItemType) => obj.Key !== prefix && obj.Key !== prefix?.slice(0, -1))
        .map((obj: S3ItemType) => ({
          Key: obj.Key!,
          isDirectory: false,
          Size: obj.Size,
          LastModified: obj.LastModified,
          ETag: obj.ETag,
          StorageClass: obj.StorageClass,
        }));

      return [...directories, ...files];
    } catch (error) {
      console.error('Error fetching sub items:', error);
      return [];
    }
  }

  public async fetchSubItemsPaginated(
    page: number = 1,
    pageSize: number = 50,
    prefix?: string,
    bucketName?: string
  ): Promise<{
    items: S3ItemType[];
    totalCount: number;
    hasMore: boolean;
  }> {
    try {
      const response: S3BrowseResponseType = await this.fetch(
        new URLSearchParams({
          bucket_name: bucketName || S3_BUCKET_NAME,
          prefix: prefix || 'backedup/',
          page: page.toString(),
          page_size: pageSize.toString(),
        })
      );

      const directories: S3ItemType[] = (response.items || [])
        .filter((item: S3ItemType) => item.isDirectory)
        .map((item: S3ItemType) => ({
          Key: item.Key!,
          isDirectory: true,
        }));

      const files: S3ItemType[] = (response.items || [])
        .filter((obj: S3ItemType) => !obj.isDirectory)
        .filter((obj: S3ItemType) => obj.Key !== prefix && obj.Key !== prefix?.slice(0, -1))
        .map((obj: S3ItemType) => ({
          Key: obj.Key!,
          isDirectory: false,
          Size: obj.Size,
          LastModified: obj.LastModified,
          ETag: obj.ETag,
          StorageClass: obj.StorageClass,
          Tags: obj.Tags,
        }));

      const allItems = [...directories, ...files];

      return {
        items: allItems,
        totalCount: response.total || allItems.length,
        hasMore: (response.total || 0) > page * pageSize,
      };
    } catch (error) {
      console.error('Error fetching paginated sub items:', error);
      return {
        items: [],
        totalCount: 0,
        hasMore: false,
      };
    }
  }

  public async createFolder(prefix?: string, bucketName?: string): Promise<void> {
    await this.apiService.createBySubPath('create-folder', {
      bucket_name: prefix,
      folder_path: bucketName,
      create_placeholder: false,
    });
    return;
  }

  public async moveAssetSource(videoSource: HierarchicalS3Item): Promise<BatchJobResponse> {
    const subPath = videoSource.isDirectory ? 'copy-asset-source-prefix' : 'copy-asset-source';
    const sourcePrefix = videoSource.Key;

    const response: BatchJobResponse = await this.apiService.createBySubPath(subPath, {
      source_bucket: S3_BUCKET_NAME,
      source_prefix: sourcePrefix,
    });
    return response;
  }

  public async renameFile(sourcePrefix: string, destPrefix: string): Promise<void> {
    await this.apiService.createBySubPath('rename-file', {
      source_bucket: S3_BUCKET_NAME,
      source_key: sourcePrefix,
      dest_key: destPrefix,
    });
    return;
  }

  private async fetch(params?: URLSearchParams): Promise<S3BrowseResponseType> {
    const response = (await this.apiService.fetch(params)) as any;
    return response;
  }
}

export const videoSourceService = new VideoSourceService(new ApiService(S3_VIDEO_SOURCE_API_PATH));
